import React from "react";
import { Card, CardBody, Row, Col } from "reactstrap";

import { Badges, H4, MUIIcons, P, SVG, ToolTip } from "../../AbstractElements";
import { ILT, VILT, WEBINAR, WBT } from "../../Api/constants";
import { Levels } from "../../utils/Constant";
import { sanitizeString } from "../../utils/helper/helper";
import { hasPermissionToComponent } from "../../utils/helper/permission";
import TagDisplay from "../TagDisplay";

import "./style.scss";

type CourseCardProps = {
  image?: string;
  domain: string[];
  levels: any;
  code: string;
  title: string;
  tags: string[];
  info: {
    audience: any;
    technician: any;
    date: string;
    duration: string;
  };
  courseType: string;
  courseId: string;
  courseRequestType?: string;
};

const CourseCard: React.FC<CourseCardProps> = ({
  image,
  domain,
  levels,
  code,
  title,
  tags,
  info,
  courseType,
  courseId,
  courseRequestType,
}) => {
  const getCourseTypeIcon = courseType => {
    switch (courseType) {
      case ILT:
        return "ilt-course-icon";
      case VILT:
        return "vilt-course-icon";
      case WEBINAR:
        return "webinar-course-icon";
      case WBT:
        return "wbt-course-icon";
      default:
        return "ilt-course-icon";
    }
  };

  const getTruncatedHeader = list => {
    if (!Array.isArray(list) || list.length === 0) return "";
    return list.length === 1 ? list[0] : `${list[0]} +${list.length - 1}`;
  };

  const getAllLevels = () => {
    if (levels && levels.length > 0) {
      return levels?.filter(label => label !== "").join(", ");
    } else return "";
  };

  const getLevelText = () => {
    if (levels && levels.length > 0) {
      if (levels.length > 1) {
        return `${levels[0]} ${levels.length - 1}+`;
      } else {
        return levels[0] ? levels[0] : `(${Levels})`.toUpperCase();
      }
    } else {
      return `(${Levels})`.toUpperCase();
    }
  };

  const isShowItem = hasPermissionToComponent(
    "COURSE_MANAGEMENT.COURSE_CARD_ITEM"
  );

  return (
    <Card className="course-card border mb-0">
      <div className="course-card__image-wrapper">
        <img
          src={image ? image : "/assets/images/course-card-default.svg"}
          alt={title}
          className="course-card__image"
          onError={e => {
            const target = e.target as HTMLImageElement;
            target.onerror = null;
            target.src = "/assets/images/course-card-default.svg";
          }}
        />

        <div className="course-card__badges">
          <div>
            <span id={`tooltip-domain-name${courseId}`}>
              {domain.length > 0 && (
                <Badges status="re_attempt" className="me-2 text-black">
                  {getTruncatedHeader(domain)}
                </Badges>
              )}
            </span>
            <ToolTip placement="top" target={`tooltip-domain-name${courseId}`}>
              {domain.join(", ")}
            </ToolTip>

            {courseRequestType && (
              <Badges status="re_attempt" className="me-2 text-black">
                {courseRequestType}
              </Badges>
            )}
          </div>
        </div>
      </div>

      <CardBody className="course-card__body">
        <div className="d-flex align-items-center gap-3">
          <div className="course-card__border-right w-50">
            <div>
              <span
                className="d-inline"
                id={sanitizeString(`tooltip-course-level-${courseId}`)}
              >
                <P className="course-card__level">{getLevelText()}</P>
              </span>
              <ToolTip
                placement="top"
                target={sanitizeString(`tooltip-course-level-${courseId}`)}
              >
                {getAllLevels()}
              </ToolTip>
            </div>
          </div>
          <div>
            <Badges status="re_attempt">
              <div className="d-flex gap-1 align-items-center">
                <div>
                  <SVG iconId={getCourseTypeIcon(courseType)} />
                </div>
                <P className="course-card__code">{code}</P>
              </div>
            </Badges>
          </div>
        </div>
        <div>
          <span id={sanitizeString(`tooltip-course-name-${courseId}`)}>
            <div className="text-ellips-parent">
              <H4
                className="course-card__title pt-2 text-ellips"
                data-fulltext={title}
              >
                {title}
              </H4>
            </div>
          </span>
          <ToolTip
            placement="top"
            target={sanitizeString(`tooltip-course-name-${courseId}`)}
          >
            {title}
          </ToolTip>
        </div>

        <div className="course-card__tags pt-2 pb-3">
          <div className="text-black">
            <div>
              <div id={`tooltip-technician${courseId}`}>
                <TagDisplay tags={tags} />
              </div>
              <ToolTip placement="top" target={`tooltip-technician${courseId}`}>
                {tags.join(", ")}
              </ToolTip>
            </div>
          </div>
        </div>

        <Row className="course-card__info p-0 m-0">
          <Col xs="6">
            <div className="info-item">
              <MUIIcons size={15} iconName={"FactoryOutlined"} />
              <div>
                <span id={`tooltip-audience${courseId}`}>
                  <div className="text-ellips-parent">
                    <span
                      className="course-card__title text-ellips"
                      data-fulltext={getTruncatedHeader(info.audience)}
                    >
                      {info.audience.length === 0
                        ? "-"
                        : getTruncatedHeader(info.audience)}
                    </span>
                  </div>
                </span>
                <ToolTip placement="top" target={`tooltip-audience${courseId}`}>
                  {info.audience.join(", ")}
                </ToolTip>
              </div>
            </div>
          </Col>
          {isShowItem && (
            <Col xs="6" className="course-card__border-left">
              <div className="info-item">
                <MUIIcons size={15} iconName={"PersonOutlined"} />
                <div>
                  <span id={`tooltip-technician${courseId}`}>
                    <div className="text-ellips-parent">
                      <span
                        className="course-card__title text-ellips"
                        data-fulltext={getTruncatedHeader(info.technician)}
                      >
                        {info.technician.length === 0
                          ? "-"
                          : getTruncatedHeader(info.technician)}
                      </span>
                    </div>
                  </span>
                  <ToolTip
                    placement="top"
                    target={`tooltip-technician${courseId}`}
                  >
                    {info.technician.join(", ")}
                  </ToolTip>
                </div>
              </div>
            </Col>
          )}
        </Row>
        <Row className="course-card__info p-0 m-0">
          {isShowItem && (
            <Col xs="6" className="course-card__border-right">
              <div className="info-item">
                <MUIIcons size={15} iconName={"CalendarTodayOutlined"} />{" "}
                {info.date}
              </div>
            </Col>
          )}
          <Col xs="6">
            <div className="info-item">
              <MUIIcons size={15} iconName={"WatchLaterOutlined"} />{" "}
              {info.duration}
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

export default CourseCard;
